import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import accountsApiService from '../../../services/accountsApiService';

const TEAL = '#5cc8c4';
const BORDER = '1px solid #000';

const STATIC = {
  sellerName: 'QUALITY COUNCIL OF INDIA',
  sellerEmail: 'accounts@nabl.qcin.org',
  sellerPan: 'AAATQ0055D',
  bankName: 'HDFC Bank',
  bankAccountNo: 'NABLQCM0573',
  ifscCode: 'HDFC0004989',
  hsnSac: '998346',
  notes: [
    'A late fee of 1% per month plus GST will be charged on any outstanding amount.',
    'The late fee will be applied starting from the 31st day after the invoice date and will continue until full payment is received.',
    'TDS under GST does not apply to invoices or payments made to the Quality Council of India, as per Section 51 of the CGST Act, 2017.',
    'Please send payment advice to accounts@nabl.qcin.org',
    'All matters are subject to Delhi jurisdiction.',
  ],
  declaration:
    'We declare that this invoice shows the actual price of the Services described and that all particulars are true and correct.',
};

const fmt = (n) =>
  parseFloat(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const UnpaidAssessmentProformaInvoice = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [d, setD] = useState(null);

  const appId = searchParams.get('appId');
  const payType = searchParams.get('payType');
  const id = searchParams.get('id');

  useEffect(() => {
    if (appId && payType && id) {
      loadInvoiceData();
    } else {
      setError('Missing required parameters: appId, payType, id');
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId, payType, id]);

  const loadInvoiceData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await accountsApiService.getUnpaidAssessmentProformaInvoice(appId, payType, id);
      if (response.success && response.data) {
        setD(response.data);
      } else {
        throw new Error(response.message || 'No data received');
      }
    } catch (err) {
      console.error('Error loading assessment proforma invoice:', err);
      setError('Failed to load invoice: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => window.print();
  const handleBack = () => navigate('/main/accounts/unpaid-invoice');

  const cell = {
    border: BORDER,
    padding: '4px 6px',
    fontSize: '11px',
    fontFamily: 'Arial, sans-serif',
    color: '#000',
    verticalAlign: 'top',
  };
  const th = { ...cell, backgroundColor: TEAL, fontWeight: 700, textAlign: 'center', verticalAlign: 'middle' };
  const label = { fontSize: '10px', fontWeight: 700 };
  const labelLight = { fontSize: '10px' };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <div style={{ width: '40px', height: '40px', border: '4px solid #e0e0e0', borderTopColor: '#00024c', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '24px', fontFamily: 'Arial, sans-serif' }}>
        <div style={{ background: '#fdecea', color: '#611a15', border: '1px solid #f5c6cb', borderRadius: '4px', padding: '12px 16px', marginBottom: '16px', fontSize: '14px' }}>
          {error}
        </div>
        <button onClick={handleBack} style={{ background: 'transparent', border: '1px solid #757575', color: '#424242', borderRadius: '4px', padding: '8px 16px', cursor: 'pointer', fontSize: '13px' }}>
          &#8592; Back to Unpaid Invoice
        </button>
      </div>
    );
  }

  if (!d) {
    return (
      <div style={{ padding: '24px', fontFamily: 'Arial, sans-serif' }}>
        <div style={{ background: '#fff3cd', color: '#856404', border: '1px solid #ffeeba', borderRadius: '4px', padding: '12px 16px', fontSize: '14px' }}>
          No invoice data available
        </div>
      </div>
    );
  }

  const nabl = d.nablAddress || {};
  const amounts = d.amounts || {};
  const gst = d.gst || {};
  const assessors = d.assessors || [];
  const tada = d.tada || [];
  const isIGST = gst.type === 'igst';

  // Assessor total from array (Amount capital A)
  const assessorTotal = assessors.reduce((sum, a) => sum + parseFloat(a.Amount || a.amount || 0), 0);
  // Overhead = basicAmount - assessorTotal
  const overheadAmount = parseFloat(11000);

  // Taxable value = basicAmount (already after TADA deduction)
  const taxableValue = parseFloat(amounts.basicAmount || 0);
  const igstAmount = parseFloat(gst.igst || 0);
  const sgstAmount = parseFloat(gst.sgst || 0);
  const cgstAmount = parseFloat(gst.cgst || 0);
  const totalTaxAmount = isIGST ? igstAmount : (sgstAmount + cgstAmount);

  const formatAddress = (addr) => {
    if (!addr) return null;
    return addr.split(/<br\s*\/?>/i).map((line, i, arr) => (
      <span key={i}>{line}{i < arr.length - 1 && <br />}</span>
    ));
  };

  return (
    <div style={{ background: '#eef0f2', padding: '24px 0', minHeight: '100%' }}>
      <div className="no-print" style={{ maxWidth: '900px', margin: '0 auto 16px', display: 'flex', justifyContent: 'space-between', gap: '12px', padding: '0 8px' }}>
        <button onClick={handleBack} style={{ background: 'transparent', border: '1px solid #757575', color: '#424242', borderRadius: '4px', padding: '8px 18px', fontSize: '13px', fontFamily: 'Arial, sans-serif', cursor: 'pointer' }}>
          &#8592; Back to Invoices
        </button>
        <button onClick={handlePrint} style={{ background: '#00024c', color: '#fff', border: 'none', borderRadius: '4px', padding: '8px 18px', fontSize: '13px', fontFamily: 'Arial, sans-serif', cursor: 'pointer' }}>
          Print Invoice
        </button>
      </div>

      <style>{`@media print { .no-print { display: none !important; } body { background: #fff; } }`}</style>

      <div style={{ maxWidth: '900px', margin: '0 auto', background: '#fff', padding: '18px 22px', boxShadow: '0 2px 14px rgba(0,0,0,0.12)', fontFamily: 'Arial, sans-serif', color: '#000' }}>

        {/* ===== Header ===== */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', paddingBottom: '10px' }}>
          <img src="/main/images/Quality_Council_of_India_Logo_2.png" alt="QCI" style={{ width: '140px' }} />
          <div style={{ fontSize: '11px', lineHeight: 1.6 }}>
            {/* <strong>{STATIC.sellerName}</strong><br /> */}
            {nabl.line1 || 'NABL, Plot No. 4, Sector-K, Institutional Area,'}<br />
            {nabl.line2 || 'Vasant Kunj, New Delhi 110 070'}<br />
            E-mail ID : {STATIC.sellerEmail}
          </div>
        </div>

        {/* ===== PAN / Invoice Type / GST bar ===== */}
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            <tr>
              <td style={{ ...cell, borderRight: 'none', width: '33%' }}>
                <span style={label}>P.A.N. No.:</span> {STATIC.sellerPan}
              </td>
              <td style={{ ...cell, borderLeft: 'none', borderRight: 'none', textAlign: 'center', fontWeight: 700, fontSize: '16px', verticalAlign: 'middle', width: '34%' }}>
                {d.invoiceType || 'TAX INVOICE'}
              </td>
              <td style={{ ...cell, borderLeft: 'none', width: '33%' }}>
                <div><span style={label}>G.S.T. No.:</span> {nabl.gstin}</div>
                <div><span style={label}>State Code :</span> {nabl.stateCode}{nabl.stateName ? ` (${nabl.stateName})` : ''}</div>
              </td>
            </tr>
          </tbody>
        </table>

        {/* ===== Buyer / Consignee ===== */}
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            <tr>
              <td style={{ ...cell, borderTop: 'none', width: '50%' }}>
                <div style={{ fontWeight: 700, marginBottom: '4px' }}>Buyer:</div>
                <div style={{ marginBottom: '4px' }}><span style={label}>Lab ID:</span> {d.labId}</div>
                <div style={{ fontWeight: 700, lineHeight: 1.3 }}>{d.labName}</div>
                <div style={{ marginTop: '4px' }}>{formatAddress(d.address)}</div>
                <div style={{ marginTop: '6px' }}>
                  <div><span style={labelLight}>State:</span> {d.state}</div>
                  {d.stateCode && <div><span style={labelLight}>State Code:</span> {d.stateCode}</div>}
                  <div><span style={labelLight}>G.S.T. No. :</span> {d.gstin}</div>
                </div>
              </td>
              <td style={{ ...cell, borderTop: 'none', borderLeft: 'none', width: '50%' }}>
                <div style={{ fontWeight: 700, marginBottom: '4px' }}>Consignee:</div>
                <div style={{ marginBottom: '4px' }}><span style={label}>Lab ID:</span> {d.labId}</div>
                <div style={{ fontWeight: 700, lineHeight: 1.3 }}>{d.labName}</div>
                <div style={{ marginTop: '4px' }}>{formatAddress(d.address)}</div>
                <div style={{ marginTop: '6px' }}>
                  <div><span style={labelLight}>State:</span> {d.state}</div>
                  {d.stateCode && <div><span style={labelLight}>State Code:</span> {d.stateCode}</div>}
                  <div><span style={labelLight}>G.S.T. No. :</span> {d.gstin}</div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        {/* ===== Invoice meta ===== */}
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            <tr>
              <td style={{ ...cell, borderTop: 'none', width: '33%' }}>
                <span style={label}>Invoice No.:</span> {d.invoiceNo}
              </td>
              <td style={{ ...cell, borderTop: 'none', borderLeft: 'none', width: '34%' }}>
                <span style={label}>Invoice Date :</span> {d.invoiceDate}
              </td>
              <td style={{ ...cell, borderTop: 'none', borderLeft: 'none', width: '33%', textAlign: 'right', fontStyle: 'italic' }}>
                Amount in INR
              </td>
            </tr>
          </tbody>
        </table>

        {/* ===== Assessment dates + HONORARIUM ===== */}
        <table style={{ width: '100%', borderCollapse: 'collapse', borderTop: 'none' }}>
          <tbody>
            {/* Date header */}
            <tr>
              <td colSpan={4} style={{ ...cell, fontWeight: 700, borderTop: 'none' }}>
                {d.assessmentDates}
              </td>
            </tr>
            {/* HONORARIUM label */}
            <tr>
              <td colSpan={4} style={{ ...cell, borderTop: 'none', fontWeight: 700, backgroundColor: '#f5f5f5' }}>
                HONORARIUM
              </td>
            </tr>
            {/* Column headers */}
            <tr>
              <th style={{ ...th, width: '6%' }}>Sr. No.</th>
              <th style={{ ...th, width: '34%', textAlign: 'left' }}>Assessor Name</th>
              <th style={{ ...th, width: '40%', textAlign: 'left' }}>Dates</th>
              <th style={{ ...th, width: '20%' }}>Amount (Rs.)</th>
            </tr>
            {/* Assessor rows */}
            {assessors.map((a, i) => (
              <tr key={i}>
                <td style={{ ...cell, textAlign: 'center' }}>{i + 1}</td>
                <td style={cell}>{a.Name}</td>
                <td style={cell}>{a.Dates || a.AssessmentDate}</td>
                <td style={{ ...cell, textAlign: 'right' }}>{fmt(a.Amount || a.amount)}</td>
              </tr>
            ))}
            {/* Honorarium subtotal */}
            <tr>
              <td colSpan={3} style={{ ...cell, fontWeight: 700, textAlign: 'right' }}>
                {fmt(assessorTotal)}
              </td>
              <td style={{ ...cell, fontWeight: 700, textAlign: 'right' }}>{fmt(assessorTotal)}</td>
            </tr>
          </tbody>
        </table>

        {/* ===== OVERHEAD CHARGES ===== */}
        {overheadAmount > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', borderTop: 'none' }}>
            <tbody>
              <tr>
                <td style={{ ...cell, borderTop: 'none', width: '80%', fontWeight: 700 }}>OVERHEAD CHARGES</td>
                <td style={{ ...cell, borderTop: 'none', borderLeft: 'none', width: '20%', textAlign: 'right', fontWeight: 700 }}>
                  {fmt(overheadAmount)}
                </td>
              </tr>
            </tbody>
          </table>
        )}

        {/* ===== TADA section ===== */}
        {d.tadaInvoice && tada.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', borderTop: 'none' }}>
            <tbody>
              <tr>
                <td colSpan={3} style={{ ...cell, borderTop: 'none', fontWeight: 700, backgroundColor: '#f5f5f5' }}>
                  TA/DA DETAILS
                </td>
              </tr>
              <tr>
                <th style={{ ...th, width: '6%' }}>Sr. No.</th>
                <th style={{ ...th, width: '74%', textAlign: 'left' }}>Assessor Name</th>
                <th style={{ ...th, width: '20%' }}>Amount (Rs.)</th>
              </tr>
              {tada.map((t, i) => (
                <tr key={i}>
                  <td style={{ ...cell, textAlign: 'center' }}>{i + 1}</td>
                  <td style={cell}>{t.name}</td>
                  <td style={{ ...cell, textAlign: 'right' }}>{fmt(t.amount)}</td>
                </tr>
              ))}
              <tr>
                <td colSpan={2} style={{ ...cell, fontWeight: 700, textAlign: 'right' }}>Total TA/DA (deducted)</td>
                <td style={{ ...cell, fontWeight: 700, textAlign: 'right' }}>(-) {fmt(d.tadaAmount)}</td>
              </tr>
            </tbody>
          </table>
        )}

        {/* ===== Amount summary ===== */}
        <table style={{ width: '100%', borderCollapse: 'collapse', borderTop: 'none' }}>
          <tbody>
            {/* GST rows */}
            {isIGST && igstAmount > 0 && (
              <tr>
                <td style={{ ...cell, borderTop: 'none', width: '60%' }}>Add IGST @ 18%</td>
                <td style={{ ...cell, borderTop: 'none', borderLeft: 'none', width: '20%', textAlign: 'center' }}>18 %</td>
                <td style={{ ...cell, borderTop: 'none', borderLeft: 'none', width: '20%', textAlign: 'right' }}>{fmt(igstAmount)}</td>
              </tr>
            )}
            {!isIGST && sgstAmount > 0 && (
              <>
                <tr>
                  <td style={{ ...cell, borderTop: 'none', width: '60%' }}>Add CGST @ 9%</td>
                  <td style={{ ...cell, borderTop: 'none', borderLeft: 'none', width: '20%', textAlign: 'center' }}>9 %</td>
                  <td style={{ ...cell, borderTop: 'none', borderLeft: 'none', width: '20%', textAlign: 'right' }}>{fmt(cgstAmount)}</td>
                </tr>
                <tr>
                  <td style={{ ...cell, borderTop: 'none', width: '60%' }}>Add SGST @ 9%</td>
                  <td style={{ ...cell, borderTop: 'none', borderLeft: 'none', width: '20%', textAlign: 'center' }}>9 %</td>
                  <td style={{ ...cell, borderTop: 'none', borderLeft: 'none', width: '20%', textAlign: 'right' }}>{fmt(sgstAmount)}</td>
                </tr>
              </>
            )}
            {/* Round off */}
            {amounts.roundOff !== null && amounts.roundOff !== undefined && (
              <tr>
                <td style={{ ...cell, borderTop: 'none', width: '60%' }}>Round Off</td>
                <td style={{ ...cell, borderTop: 'none', borderLeft: 'none', width: '20%' }}></td>
                <td style={{ ...cell, borderTop: 'none', borderLeft: 'none', width: '20%', textAlign: 'right' }}>{amounts.roundOff}</td>
              </tr>
            )}
            {/* Total */}
            <tr>
              <td style={{ ...cell, borderTop: 'none', fontWeight: 700, width: '60%' }}>Total</td>
              <td style={{ ...cell, borderTop: 'none', borderLeft: 'none', width: '20%' }}></td>
              <td style={{ ...cell, borderTop: 'none', borderLeft: 'none', fontWeight: 700, width: '20%', textAlign: 'right' }}>
                {fmt(amounts.grandTotal)}
              </td>
            </tr>
            {/* Amount in words */}
            <tr>
              <td colSpan={3} style={{ ...cell, borderTop: 'none' }}>
                <span style={{ fontWeight: 700 }}>Amount Chargeable (in words)</span>
                <span style={{ marginLeft: '16px', fontStyle: 'italic' }}>E. &amp;O.E</span>
                <div style={{ fontWeight: 700, marginTop: '4px' }}>{amounts.inWords}</div>
              </td>
            </tr>
          </tbody>
        </table>

        {/* ===== HSN/SAC Summary ===== */}
        <div style={{ ...cell, fontWeight: 700, borderTop: 'none' }}>HSN/SAC Summary</div>
        {isIGST ? (
          <table style={{ width: '100%', borderCollapse: 'collapse', borderTop: 'none' }}>
            <thead>
              <tr>
                <th style={{ ...th, width: '12%' }}>HSN/SAC</th>
                <th style={{ ...th, width: '28%' }}>Taxable Value</th>
                <th style={th} colSpan={2}>Integrated Tax</th>
                <th style={{ ...th, width: '20%' }}>Total Tax Amount</th>
              </tr>
              <tr>
                <th style={th}></th>
                <th style={th}></th>
                <th style={{ ...th, width: '10%' }}>Rate</th>
                <th style={{ ...th, width: '15%' }}>Amount</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ ...cell, textAlign: 'center' }}>{STATIC.hsnSac}</td>
                <td style={{ ...cell, textAlign: 'right' }}>{fmt(taxableValue)}</td>
                <td style={{ ...cell, textAlign: 'center' }}>18%</td>
                <td style={{ ...cell, textAlign: 'right' }}>{fmt(igstAmount)}</td>
                <td style={{ ...cell, textAlign: 'right' }}>{fmt(igstAmount)}</td>
              </tr>
              <tr>
                <td style={{ ...cell, fontWeight: 700, textAlign: 'center' }}>Total</td>
                <td style={{ ...cell, fontWeight: 700, textAlign: 'right' }}>{fmt(taxableValue)}</td>
                <td style={cell}></td>
                <td style={{ ...cell, fontWeight: 700, textAlign: 'right' }}>{fmt(igstAmount)}</td>
                <td style={{ ...cell, fontWeight: 700, textAlign: 'right' }}>{fmt(igstAmount)}</td>
              </tr>
            </tbody>
          </table>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', borderTop: 'none' }}>
            <thead>
              <tr>
                <th style={{ ...th, width: '12%' }}>HSN/SAC</th>
                <th style={{ ...th, width: '22%' }}>Taxable Value</th>
                <th style={th} colSpan={2}>CGST</th>
                <th style={th} colSpan={2}>SGST</th>
                <th style={{ ...th, width: '16%' }}>Total Tax Amount</th>
              </tr>
              <tr>
                <th style={th}></th>
                <th style={th}></th>
                <th style={{ ...th, width: '8%' }}>Rate</th>
                <th style={{ ...th, width: '12%' }}>Amount</th>
                <th style={{ ...th, width: '8%' }}>Rate</th>
                <th style={{ ...th, width: '12%' }}>Amount</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ ...cell, textAlign: 'center' }}>{STATIC.hsnSac}</td>
                <td style={{ ...cell, textAlign: 'right' }}>{fmt(taxableValue)}</td>
                <td style={{ ...cell, textAlign: 'center' }}>9%</td>
                <td style={{ ...cell, textAlign: 'right' }}>{fmt(cgstAmount)}</td>
                <td style={{ ...cell, textAlign: 'center' }}>9%</td>
                <td style={{ ...cell, textAlign: 'right' }}>{fmt(sgstAmount)}</td>
                <td style={{ ...cell, textAlign: 'right' }}>{fmt(totalTaxAmount)}</td>
              </tr>
              <tr>
                <td style={{ ...cell, fontWeight: 700, textAlign: 'center' }}>Total</td>
                <td style={{ ...cell, fontWeight: 700, textAlign: 'right' }}>{fmt(taxableValue)}</td>
                <td style={cell}></td>
                <td style={{ ...cell, fontWeight: 700, textAlign: 'right' }}>{fmt(cgstAmount)}</td>
                <td style={cell}></td>
                <td style={{ ...cell, fontWeight: 700, textAlign: 'right' }}>{fmt(sgstAmount)}</td>
                <td style={{ ...cell, fontWeight: 700, textAlign: 'right' }}>{fmt(totalTaxAmount)}</td>
              </tr>
            </tbody>
          </table>
        )}

        {/* ===== Remarks + Bank ===== */}
        <table style={{ width: '100%', borderCollapse: 'collapse', borderTop: 'none' }}>
          <tbody>
            <tr>
              <td style={{ ...cell, width: '60%', minHeight: '80px' }}>
                <span style={{ fontWeight: 700 }}>Remarks :</span> {d.remarks}
              </td>
              <td style={{ ...cell, borderLeft: 'none', width: '40%', verticalAlign: 'top' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontWeight: 700 }}>Bank Name :</span>
                  <span>{STATIC.bankName}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                  <span style={{ fontWeight: 700 }}>Bank Account No.:</span>
                  <span>{STATIC.bankAccountNo}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                  <span style={{ fontWeight: 700 }}>IFSC Code:</span>
                  <span>{STATIC.ifscCode}</span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        {/* ===== Notes + Declaration ===== */}
        <div style={{ ...cell, borderTop: 'none', fontSize: '10px', lineHeight: 1.6 }}>
          <div style={{ fontWeight: 700, marginBottom: '4px' }}>Please Note:</div>
          {STATIC.notes.map((n, i) => (
            <div key={i}>{i + 1}. {n}</div>
          ))}
          <div style={{ fontWeight: 700, marginTop: '10px', marginBottom: '2px' }}>Declaration:</div>
          <div>{STATIC.declaration}</div>
        </div>

        {/* ===== Footer ===== */}
        <div style={{ textAlign: 'center', fontWeight: 700, fontSize: '11px', marginTop: '18px', paddingTop: '10px' }}>
          This is a computer-generated invoice and does not require a signature.
        </div>
      </div>
    </div>
  );
};

export default UnpaidAssessmentProformaInvoice;